import {
  getLocalStorageJson,
  setLocalStorageJson,
} from "@redotech/redo-web/utils/local-storage-wrapper";
import * as z from "zod";

/**
 * Dismissal history lives in this browser only: the offer has no server-side
 * popup type, and a repeat on another device is an acceptable price for that.
 */
const STORAGE_KEY_PREFIX = "redo.credit-incentive";

const historySchema = z.object({
  dismissals: z.number().int().nonnegative(),
  lastDismissal: z.string().datetime().nullable(),
  completed: z.boolean(),
});
export type CreditIncentiveHistory = z.infer<typeof historySchema>;

export const CREDIT_INCENTIVE_MAX_SHOWINGS = 2;
/** Instants only take time units, so a week is spelled in hours. */
const COOLDOWN_HOURS = Temporal.Duration.from({ days: 7 }).total({
  unit: "hours",
});

export const EMPTY_HISTORY: CreditIncentiveHistory = {
  dismissals: 0,
  lastDismissal: null,
  completed: false,
};

/** Booked (completed) never returns; a dismissal waits a week and comes back once. */
export function canShowCreditIncentive(
  history: CreditIncentiveHistory,
  now: Temporal.Instant,
): boolean {
  if (history.completed) {
    return false;
  }
  if (history.dismissals >= CREDIT_INCENTIVE_MAX_SHOWINGS) {
    return false;
  }
  if (history.lastDismissal === null) {
    return true;
  }
  const eligibleAgainAt = Temporal.Instant.from(history.lastDismissal).add({
    hours: COOLDOWN_HOURS,
  });
  return Temporal.Instant.compare(now, eligibleAgainAt) >= 0;
}

export function recordDismissal(
  history: CreditIncentiveHistory,
  now: Temporal.Instant,
): CreditIncentiveHistory {
  return {
    ...history,
    dismissals: history.dismissals + 1,
    lastDismissal: now.toString(),
  };
}

export function recordCompletion(
  history: CreditIncentiveHistory,
): CreditIncentiveHistory {
  return { ...history, completed: true };
}

function storageKey(teamId: string, userId: string): string {
  return `${STORAGE_KEY_PREFIX}.${teamId}.${userId}`;
}

export function readCreditIncentiveHistory(
  teamId: string,
  userId: string,
): CreditIncentiveHistory {
  try {
    const parsed = historySchema.safeParse(
      getLocalStorageJson(storageKey(teamId, userId)),
    );
    return parsed.success ? parsed.data : EMPTY_HISTORY;
  } catch {
    return EMPTY_HISTORY;
  }
}

export function writeCreditIncentiveHistory(
  teamId: string,
  userId: string,
  history: CreditIncentiveHistory,
): void {
  try {
    setLocalStorageJson(storageKey(teamId, userId), history);
  } catch {
    // Private mode or a full store: the modal may show again later.
  }
}
